import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import {
  UserCircle, PencilSimple, Lock, Star, Trash, Spinner, CheckCircle, ArrowSquareOut,
  TrendUp, TrendDown, CurrencyBtc, ShieldCheck, EnvelopeSimple, Clock, Bell, FloppyDisk
} from "@phosphor-icons/react";
import GoldTierPanel from "@/components/GoldTierPanel";
import AIUsagePanel from "@/components/AIUsagePanel";

function formatPrice(price) {
  if (!price && price !== 0) return "$0.00";
  if (price >= 1000) return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.001) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(6)}`;
}

function formatApiError(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong";
}

export default function ProfilePage() {
  const { user, logout, checkAuth, updateWatchlist } = useAuth();
  const navigate = useNavigate();

  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg, setNameMsg] = useState("");

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: "", text: "" });

  const [watchlistAssets, setWatchlistAssets] = useState([]);
  const [wlLoading, setWlLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [snapshotCount, setSnapshotCount] = useState(0);

  useEffect(() => {
    if (user === false) navigate("/auth");
  }, [user, navigate]);

  const fetchWatchlistPrices = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/watchlist/prices`, { withCredentials: true });
      setWatchlistAssets(r.data.assets || []);
    } catch (err) {
      console.error("Failed to fetch watchlist prices:", err);
    }
    setWlLoading(false);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const [alertsRes, snapsRes] = await Promise.all([
        axios.get(`${API}/alerts`, { withCredentials: true }).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API}/budget/snapshots`, { withCredentials: true }).catch(() => ({ data: { snapshots: [] } })),
      ]);
      setAlerts(alertsRes.data.alerts || []);
      setSnapshotCount((snapsRes.data.snapshots || []).length);
    } catch (err) {
      console.error("Failed to fetch profile stats:", err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setNewName(user.name || "");
      fetchWatchlistPrices();
      fetchStats();
    }
  }, [user, fetchWatchlistPrices, fetchStats]);

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`, { withCredentials: true });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      console.error("Failed to delete alert:", err);
    }
  };

  const saveName = async () => {
    if (!newName.trim()) return;
    setNameLoading(true);
    setNameMsg("");
    try {
      await axios.put(`${API}/auth/profile`, { name: newName.trim() }, { withCredentials: true });
      await checkAuth();
      setEditingName(false);
      setNameMsg("Name updated!");
      setTimeout(() => setNameMsg(""), 3000);
    } catch (e) {
      setNameMsg(formatApiError(e.response?.data?.detail));
    }
    setNameLoading(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ type: "", text: "" });
    if (newPw.length < 6) {
      setPwMsg({ type: "error", text: "New password must be at least 6 characters" });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ type: "error", text: "Passwords do not match" });
      return;
    }
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw }, { withCredentials: true });
      setPwMsg({ type: "success", text: "Password changed successfully!" });
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      setPwMsg({ type: "error", text: formatApiError(e.response?.data?.detail) });
    }
    setPwLoading(false);
  };

  const removeFromWatchlist = async (assetId) => {
    try {
      await updateWatchlist(assetId, "remove");
      setWatchlistAssets(prev => prev.filter(a => a.id !== assetId));
    } catch (err) {
      console.error("Failed to remove from watchlist:", err);
    }
  };

  if (!user) return null;

  return (
    <div className="p-5 max-w-[900px] mx-auto space-y-6" data-testid="profile-page">
      {/* Profile Header */}
      <div className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-6" data-testid="profile-header">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
            <UserCircle size={36} weight="thin" className="text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    maxLength={50}
                    autoFocus
                    data-testid="edit-name-input"
                    className="bg-zinc-950 border border-zinc-700 rounded-sm px-2 py-1 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/50 w-48"
                  />
                  <button onClick={saveName} disabled={nameLoading} data-testid="save-name-btn" className="text-[10px] text-amber-300 bg-amber-500/15 border border-amber-500/40 px-2 py-1 rounded-sm hover:bg-amber-500/25">
                    {nameLoading ? <Spinner size={10} className="animate-spin" /> : "Save"}
                  </button>
                  <button onClick={() => { setEditingName(false); setNewName(user.name || ""); }} className="text-[10px] text-zinc-500 px-2 py-1">Cancel</button>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-zinc-50">{user.name || "User"}</h2>
                  <button onClick={() => setEditingName(true)} data-testid="edit-name-btn" className="text-zinc-600 hover:text-zinc-400 transition-colors">
                    <PencilSimple size={14} />
                  </button>
                </>
              )}
            </div>
            {nameMsg && <span className="text-[11px] text-emerald-400">{nameMsg}</span>}
            <div className="flex items-center gap-4 mt-1 text-[11px] text-zinc-500">
              <span className="flex items-center gap-1"><EnvelopeSimple size={11} /> {user.email}</span>
              <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-900/30 text-amber-400">{user.role?.toUpperCase()}</Badge>
              {user.created_at && <span className="flex items-center gap-1"><Clock size={10} /> Joined {new Date(user.created_at).toLocaleDateString()}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3" data-testid="profile-stats">
        <div className="bg-zinc-900/40 border border-amber-500/15 rounded-sm p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Star size={12} weight="fill" className="text-amber-400" />
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Watchlist</span>
          </div>
          <p className="text-lg font-data font-bold text-zinc-100">{user.watchlist?.length || 0}</p>
        </div>
        <div className="bg-zinc-900/40 border border-amber-500/15 rounded-sm p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Bell size={12} className="text-amber-400" />
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Alerts</span>
          </div>
          <p className="text-lg font-data font-bold text-zinc-100">
            {alerts.filter(a => !a.triggered).length}
            <span className="text-[10px] text-zinc-500 font-normal ml-1">active</span>
          </p>
        </div>
        <div className="bg-zinc-900/40 border border-amber-500/15 rounded-sm p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <FloppyDisk size={12} className="text-amber-400" />
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Budget</span>
          </div>
          <p className="text-lg font-data font-bold text-zinc-100">
            {snapshotCount}
            <span className="text-[10px] text-zinc-500 font-normal ml-1">snapshots</span>
          </p>
        </div>
      </div>

      {/* Gold Tier & Silly Goose access */}
      <GoldTierPanel currentUser={user} />

      {/* Alerts */}
      <div className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-6" data-testid="profile-alerts">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={16} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-zinc-200">Price Alerts</h3>
          <span className="text-[10px] text-zinc-600">({alerts.length} total)</span>
        </div>
        {alerts.length === 0 ? (
          <div className="text-center py-6">
            <Bell size={24} className="text-zinc-700 mx-auto mb-2" />
            <p className="text-xs text-zinc-500">No alerts set up.</p>
            <p className="text-[10px] text-zinc-600 mt-1">Click "Set Alert" on any asset detail page to get notified when it crosses a target price.</p>
          </div>
        ) : (
          <div className="space-y-1" data-testid="alerts-list">
            {alerts.map(a => {
              const isAbove = a.direction === "above";
              const status = a.triggered ? "triggered" : "active";
              const statusColor = a.triggered ? "text-emerald-400" : "text-amber-400";
              return (
                <div key={a.id} className="flex items-center justify-between py-2 px-3 bg-zinc-950/40 border border-zinc-800/40 rounded-sm hover:border-zinc-700 transition-colors group" data-testid={`alert-row-${a.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {isAbove ? <TrendUp size={12} className="text-emerald-400 shrink-0" /> : <TrendDown size={12} className="text-amber-400 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium text-zinc-200 truncate">{a.label}</p>
                      <p className="text-[9px] text-zinc-600">
                        <span className={statusColor}>{status.toUpperCase()}</span>
                        {a.triggered && a.triggered_at && <> · {new Date(a.triggered_at).toLocaleDateString()}</>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[11px] font-data text-zinc-400">${a.price?.toLocaleString()}</span>
                    <button
                      onClick={() => deleteAlert(a.id)}
                      data-testid={`alert-delete-${a.id}`}
                      className="text-zinc-700 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete alert"
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Watchlist */}
      <div className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-6" data-testid="profile-watchlist">
        <div className="flex items-center gap-2 mb-4">
          <Star size={16} weight="fill" className="text-amber-400" />
          <h3 className="text-sm font-semibold text-zinc-200">My Watchlist</h3>
          <span className="text-[10px] text-zinc-600">({user.watchlist?.length || 0} assets)</span>
        </div>

        {wlLoading ? (
          <div className="space-y-2">
            {[1,2,3].map(i => <div key={i} className="h-12 bg-zinc-800/40 rounded-sm animate-pulse" />)}
          </div>
        ) : watchlistAssets.length > 0 ? (
          <div className="space-y-1" data-testid="watchlist-items">
            {watchlistAssets.map(asset => {
              const isUp = asset.change_24h > 0;
              return (
                <div key={asset.id} className="flex items-center justify-between py-2.5 px-3 bg-zinc-950/40 border border-zinc-800/40 rounded-sm hover:border-zinc-700 transition-colors group" data-testid={`wl-item-${asset.id}`}>
                  <div className="flex items-center gap-3">
                    <CurrencyBtc size={14} className="text-zinc-600" />
                    <div>
                      <span className="text-[12px] font-medium text-zinc-200">{asset.symbol}</span>
                      <span className="text-[10px] text-zinc-600 ml-2">{asset.name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[12px] font-data text-zinc-300">{formatPrice(asset.current_price)}</span>
                    <span className={`text-[11px] font-data flex items-center gap-0.5 w-20 justify-end ${isUp ? "text-emerald-400" : "text-red-400"}`}>
                      {isUp ? <TrendUp size={10} /> : <TrendDown size={10} />}
                      {isUp ? "+" : ""}{asset.change_24h?.toFixed(2)}%
                    </span>
                    <button
                      onClick={() => navigate(`/asset/${asset.type || "crypto"}/${encodeURIComponent(asset.id)}`)}
                      className="text-zinc-600 hover:text-amber-400 transition-colors"
                      title="View details"
                    >
                      <ArrowSquareOut size={13} />
                    </button>
                    <button
                      onClick={() => removeFromWatchlist(asset.id)}
                      data-testid={`wl-remove-${asset.id}`}
                      className="text-zinc-700 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                      title="Remove from watchlist"
                    >
                      <Trash size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Star size={28} className="text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No assets in your watchlist yet.</p>
            <p className="text-[11px] text-zinc-600 mt-1">Click the star on any asset card on the Dashboard to add it.</p>
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-zinc-900/50 border border-amber-500/20 rounded-sm p-6" data-testid="profile-change-password">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={16} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-zinc-200">Change Password</h3>
        </div>

        <form onSubmit={changePassword} className="space-y-3 max-w-sm">
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">Current Password</label>
            <input
              type="password"
              value={currentPw}
              onChange={e => setCurrentPw(e.target.value)}
              required
              data-testid="current-password"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">New Password</label>
            <input
              type="password"
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              required
              minLength={6}
              placeholder="Min 6 characters"
              data-testid="new-password"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              required
              minLength={6}
              data-testid="confirm-new-password"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>

          {pwMsg.text && (
            <p className={`text-xs px-3 py-2 rounded-sm border ${pwMsg.type === "success" ? "text-emerald-400 bg-emerald-900/20 border-emerald-800/30" : "text-red-400 bg-red-900/20 border-red-800/30"}`} data-testid="pw-message">
              {pwMsg.type === "success" && <CheckCircle size={12} className="inline mr-1" weight="fill" />}
              {pwMsg.text}
            </p>
          )}

          <button
            type="submit"
            disabled={pwLoading}
            data-testid="change-password-btn"
            className="bg-amber-500/15 border border-amber-500/40 text-amber-300 text-sm font-medium px-4 py-2 rounded-sm hover:bg-amber-500/25 transition-colors disabled:opacity-40 flex items-center gap-2"
          >
            {pwLoading && <Spinner size={12} className="animate-spin" />}
            Change Password
          </button>
        </form>
      </div>

      {/* Account Actions */}
      <div className="bg-zinc-900/50 border border-zinc-800/40 rounded-sm p-6" data-testid="profile-actions">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={16} className="text-zinc-500" />
          <h3 className="text-sm font-semibold text-zinc-400">Account</h3>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="profile-logout-btn"
          className="text-sm text-red-400/70 hover:text-red-400 transition-colors border border-red-800/30 hover:border-red-800/50 px-4 py-2 rounded-sm"
        >
          Sign Out
        </button>
      </div>

      {user.role === "admin" && <AdminResetLinkTool />}
      <AIUsagePanel />
    </div>
  );
}


function AdminResetLinkTool() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    setLink("");
    try {
      const r = await axios.post(`${API}/auth/admin/reset-link`, { email: email.trim() }, { withCredentials: true });
      setLink(r.data.reset_link);
      setExpiresAt(r.data.expires_at);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate link");
    }
    setLoading(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Clipboard write failed:", err);
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-amber-500/30 rounded-sm p-6" data-testid="admin-reset-tool">
      <div className="flex items-center gap-2 mb-3">
        <Lock size={16} className="text-amber-400" />
        <h3 className="text-sm font-semibold text-zinc-200">Admin · Generate Reset Link</h3>
        <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 bg-amber-900/30 text-amber-400">ADMIN</Badge>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">
        Resend free tier only delivers emails to the verified account owner.
        Use this tool to generate reset links for other users and share them manually (via text, chat, etc.). Links expire in 1 hour.
      </p>
      <form onSubmit={generate} className="flex gap-2 max-w-md">
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="user@example.com"
          data-testid="admin-reset-email"
          className="flex-1 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
          required
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-reset-generate"
          className="bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-medium px-4 py-2 rounded-sm hover:bg-amber-500/25 transition-colors disabled:opacity-40 whitespace-nowrap"
        >
          {loading ? "Generating..." : "Generate Link"}
        </button>
      </form>
      {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
      {link && (
        <div className="mt-4 p-3 bg-zinc-950/60 border border-amber-500/20 rounded-sm" data-testid="admin-reset-result">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Reset Link (share with user)</span>
            <span className="text-[9px] text-zinc-600">Expires {new Date(expiresAt).toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={link}
              onFocus={e => e.target.select()}
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-[10px] font-data text-amber-200 focus:outline-none"
              data-testid="admin-reset-link-value"
            />
            <button
              onClick={copyLink}
              data-testid="admin-reset-copy"
              className="text-[10px] px-2 py-1.5 rounded-sm border border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800 transition-colors whitespace-nowrap"
            >
              {copied ? <><CheckCircle size={10} weight="fill" className="inline mr-1 text-emerald-400" /> Copied</> : "Copy"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
